<template>
  <v-container fluid>
    <v-row>
      <!-- KPI Card -->
      <v-col cols="12" md="6" lg="4">
        <v-card elevation="2" color="primary" class="text-white">
          <v-card-title>
            <v-icon class="mr-2" color="white">mdi-chart-box</v-icon>
            KPI
          </v-card-title>

          <v-card-text>
            <div v-if="loadingKpis" class="text-center py-4">
              <v-progress-circular indeterminate color="white" size="24"></v-progress-circular>
            </div>
            <ul v-else class="kpi-list">
              <li>
                <span class="kpi-name">
                  <v-icon size="18" class="mr-1" color="white">mdi-alert-circle</v-icon>
                  Overdue Tasks
                </span>
                <span class="kpi-value" :class="{ 'text-error': kpis.overdueTasksCount > 0 }">
                  {{ kpis.overdueTasksCount }}
                </span>
              </li>

              <li>
                <span class="kpi-name">
                  <v-icon size="18" class="mr-1" color="white">mdi-timer-outline</v-icon>
                  Avg Task Resolution Time
                </span>
                <span class="kpi-value">{{ formatDays(kpis.averageTaskResolutionDays) }}</span>
              </li>

              <li>
                <span class="kpi-name">
                  <v-icon size="18" class="mr-1" color="white">mdi-file-alert-outline</v-icon>
                  Pending Findings
                </span>
                <span class="kpi-value" :class="{ 'text-warning': kpis.pendingFindingsCount > 0 }">
                  {{ kpis.pendingFindingsCount }}
                </span>
              </li>

              <li class="kpi-wo-block">
                <div class="kpi-wo-header">
                  <v-icon size="16" class="mr-1" color="white">mdi-briefcase-outline</v-icon>
                  Work Orders
                </div>
                <div class="kpi-wo-stats">
                  <div class="kpi-wo-stat">
                    <span class="kpi-wo-label">Active</span>
                    <span class="kpi-wo-value">{{ kpis.activeWorkOrdersCount }}</span>
                  </div>
                  <div class="kpi-wo-stat">
                    <span class="kpi-wo-label">Overdue</span>
                    <span
                      class="kpi-wo-value"
                      :class="{ 'text-error': kpis.overdueWorkOrdersCount > 0 }"
                    >
                      {{ kpis.overdueWorkOrdersCount }}
                    </span>
                  </div>
                  <div class="kpi-wo-stat">
                    <span class="kpi-wo-label">Closed / wk</span>
                    <span class="kpi-wo-value">{{ kpis.workOrdersClosedThisWeek }}</span>
                  </div>
                </div>
              </li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Middle card -->
      <v-col cols="12" md="6" lg="4">
        <v-card elevation="2" color="green-darken-1" class="text-white">
          <v-card-title>
            <v-icon class="mr-2" color="white">mdi-account-clock</v-icon>
            Recent Logins
          </v-card-title>

          <v-card-text>
            <div v-if="recentUsers.length === 0" class="text-body-2">No recent logins.</div>

            <v-list density="compact" class="bg-transparent pa-0">
              <v-list-item v-for="u in recentUsers" :key="u.id" class="px-0">
                <template #prepend>
                  <v-avatar size="28" class="bg-white">
                    <v-img :src="getUserIconUrl(u.iconId)" />
                  </v-avatar>
                </template>

                <v-list-item-title class="text-white">
                  {{ u.userName || u.name || u.email }}
                </v-list-item-title>

                <v-list-item-subtitle class="text-white" style="opacity: 0.85">
                  {{ formatLogin(u.lastLogin) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- My Workload -->
      <v-col cols="12" md="6" lg="4">
        <v-card elevation="2" color="blue-grey-darken-2" class="text-white">
          <v-card-title>
            <v-icon class="mr-2" color="yellow">mdi-briefcase-outline</v-icon>
            My Workload
          </v-card-title>
          <v-card-text>
            <div v-if="loadingWorkload" class="text-center py-4">
              <v-progress-circular indeterminate color="white" size="24"></v-progress-circular>
            </div>
            <ul v-else class="workload-list">
              <li>
                <router-link :to="{ name: 'TaskList' }" class="workload-row">
                  <span class="workload-name">
                    <v-icon size="18" class="mr-1" color="white">mdi-clipboard-list-outline</v-icon>
                    Tasks assigned to me
                  </span>
                  <span class="workload-value">{{ workloadTasksCount }}</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'TaskList', query: { overdue: '1' } }" class="workload-row">
                  <span class="workload-name">
                    <v-icon size="18" class="mr-1" color="white">mdi-alert-circle-outline</v-icon>
                    Overdue (mine)
                  </span>
                  <span class="workload-value" :class="{ 'text-error': workloadOverdueCount > 0 }">{{ workloadOverdueCount }}</span>
                </router-link>
              </li>
              <li class="workload-row">
                <span class="workload-name">
                  <v-icon size="18" class="mr-1" color="white">mdi-file-alert-outline</v-icon>
                  Pending findings (mine)
                </span>
                <span class="workload-value" :class="{ 'text-warning': kpis.pendingFindingsCount > 0 }">{{ kpis.pendingFindingsCount }}</span>
              </li>
              <li class="workload-row">
                <span class="workload-name">
                  <v-icon size="18" class="mr-1" color="white">mdi-cellphone-sync</v-icon>
                  Last refreshed
                </span>
                <span class="workload-caption">{{ formatLastMobileSync(lastMobileSyncAt) }}</span>
              </li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- LEFT COLUMN: 7 wide -->
      <v-col cols="12" md="6" lg="6">
        <v-row>
          <v-col cols="12">
            <v-card elevation="2">
              <task-table
                class="task-table"
                title="Tasks Due Soon <small>(<i>Next 7 days</i>)</small>"
                :headers="headers"
                :items="tasks"
                type="task"
                id-key="taskId"
                :externalHoverId="hoverId"
                @row-enter="hoverId = $event.id"
                @row-leave="hoverId = null"
                @row-click="focusFromGrid"
              />
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card elevation="2">
              <task-table
                class="task-table"
                title="Findings Requiring Action <small> (<i>Status: Action Required</i>)</small>"
                :headers="headers2"
                :items="findings"
                type="finding"
                id-key="findingId"
                :externalHoverId="hoverId"
                @row-enter="hoverId = $event.id"
                @row-leave="hoverId = null"
                @row-click="focusFromGrid"
              />
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card elevation="2">
              <task-table
                class="task-table"
                title="Upcoming Inspections
              <small>(<i>Status: Planned</i>)</small>"
                :headers="headers3"
                :items="upcomingInspections"
                type="inspection"
                id-key="inspectionId"
                :externalHoverId="hoverInspId"
                :externalHoverIds="hoverInspIds"
                @row-click="goToInspection"
                @row-enter="onInspectionRowEnter"
                @row-leave="onInspectionRowLeave"
              />
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- RIGHT COLUMN: Map -->
      <v-col cols="12" md="6" lg="6" class="d-flex">
        <div :class="['map-wrapper', { 'is-expanded': mapExpanded }]">
          <WayMap
            ref="map"
            :items="filteredMapItems"
            :geojson="filteredGeojson"
            :geoHoverId="hoverZoneId"
            :selectedCrs="settings.selectedCrs"
            :typeStyles="typeStyles"
            :showAccuracy="false"
            :highlightId="hoverId"
            :show-zone-labels="false"
            :tile-layer="currentTileLayerConfig"
            @shape-hover="onShapeHover"
            @shape-leave="onShapeLeave"
            @shape-click="onShapeClick"
            @marker-hover="hoverId = $event.id"
            @marker-leave="hoverId = null"
            @marker-click="onMarkerClick"
            @layer-ready="onMapReady"
          />

          <!-- expand/exit button; stop events so it never interferes with Leaflet -->
          <v-btn
            icon
            variant="tonal"
            density="comfortable"
            class="map-expand-btn"
            :title="mapExpanded ? 'Exit full screen' : 'Expand'"
            @click.stop="toggleMapExpanded"
            @mousedown.stop
            @touchstart.stop
          >
            <v-icon>{{ mapExpanded ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}</v-icon>
          </v-btn>

          <!-- Tile Layer Control Panel (only show in expanded mode, positioned below expand button) -->
          <MapTileLayerControl
            v-if="mapExpanded"
            :map-expanded="mapExpanded"
            :current-tile-layer="currentTileLayerId"
            class="map-tile-control-overview"
            @update:tile-layer="onTileLayerChange"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>

  <!-- Details modal -->
  <v-dialog v-model="detailsOpen" max-width="900px" scrollable>
    <!-- Show task details when a task is selected. Same props/events as the
         normal task record (Tasks grid) so it looks and edits identically. -->
    <TaskDetailPanel
      v-if="selectedType === 'task' && selected"
      :task="selected"
      :assignees="assignees"
      :statuses="taskStatuses"
      :priorities="taskPriorities"
      closable
      @close="detailsOpen = false"
      @request-edit="onEditTask"
      @completed="onTaskChanged"
      @save="onTaskChanged"
      @deleted="onTaskChanged"
      @comment-posted="fetchTasksDue"
    />

    <!-- Finding details (reusable summary card) -->
    <FindingSummaryCard
      v-else-if="selectedType === 'finding' && selected"
      :finding="selected"
      :status-label="'Action Needed'"
      :status-color="'red-lighten-4'"
      :status-text-color="'red-darken-2'"
      closable
      @close="detailsOpen = false"
      :show-map="Boolean(selected?.latitude && selected?.longitude)"
      :map-zoom="12"
    />
  </v-dialog>
</template>

<script>
import TaskTable from "@/components/DashboardTaskTable.vue";
//import MapView from "@/components/MapView1.vue";
import WayMap from "@/components/WayMap.vue";
import { getTasksDue, getTasksByUser } from "@/api/tasks";
import { getFindingByStatus } from "@/api/findings";
import { getInspectionByStatus } from "@/api/inspections";
import { fetchFormsAll } from "@/api/formsTemplates";
import { icons, iconsNums } from "@/assets/icons";
import shovelPng from "@/assets/icons/shovel.png";
import TaskDetailPanel from "@/components/TaskDetailPanel.vue";
import FindingSummaryCard from "@/components/FindingSummaryDetailPanel.vue";
import * as teamApi from "@/api/teams";
import { getLookupDataByType } from "@/api/lookups";
import { fetchZones, fetchZoneGeoJson } from "@/api/zones";
import dashboardApi from "@/api/dashboard";
import pmApi from "@/api/pm";
import MapTileLayerControl from "@/components/MapTileLayerControl.vue";

export default {
  components: { WayMap, TaskTable, TaskDetailPanel, FindingSummaryCard, fetchZones, MapTileLayerControl },
  data() {
    return {
      settings: { selectedCrs: "EPSG:2157" }, // ITM default
      typeStyles: {
        task: { color: "#2a72cf", icon: "fas fa-tasks" },
        finding: { color: "#f2994a", icon: "fas fa-exclamation-circle" },
        inspection: { color: "#27ae60", icon: "fas fa-clipboard-check" },
        asset: { color: "#9c27b0", icon: "fas fa-map-marker-alt" },
      },
      headers: [
        { title: "Title", key: "title", sortable: true },
        { title: "Assigned to", key: "assigned_to", sortable: false },
        { title: "Due Date", key: "dueDate", sortable: true },
        { title: "Priority", key: "priorityFK", sortable: true },
      ],
      headers2: [
        { title: "Title", key: "title", sortable: true },
        { title: "Location", key: "zoneNameFK", sortable: false },
        { title: "Submitted Date", key: "submitTime", sortable: true },
        { title: "Priority", key: "priorityFK", sortable: true },
      ],
      headers3: [
        { title: "Title", key: "name", sortable: true },
        { title: "Owner", key: "ownerFK", sortable: false },
        { title: "Due Date", key: "dueDate", sortable: true },
        { title: "Location", key: "zoneNameFK", sortable: true },
      ],
      tasks: [],
      findings: [],
      upcomingInspections: [],
      hoverId: null,
      detailsOpen: false,
      selected: null,
      selectedType: null,
      // Passed to TaskDetailPanel so its Edit mode (assignee/status/priority
      // dropdowns) works the same as the normal task record.
      assignees: [],
      taskStatuses: [],
      taskPriorities: [],
      recentUsers: [],
      formVisualsById: {},
      zones: [],
      overlays: {},
      hoverZoneId: null,
      hoverInspId: null,
      hoverInspIds: [],
      mapExpanded: false,
      leafletMap: null,
      loadingKpis: false,
      loadingWorkload: false,
      userTasks: [],
      lastMobileSyncAt: null,
      kpis: {
        overdueTasksCount: 0,
        averageTaskResolutionDays: 0,
        tasksCompletedThisWeek: 0,
        highPriorityOverdueCount: 0,
        workOrdersClosedThisWeek: 0,
        activeWorkOrdersCount: 0,
        overdueWorkOrdersCount: 0,
        workOrderOnTimeRate: 0,
        pendingFindingsCount: 0,
      },
      // Map layer visibility - zones & inspections off by default
      mapLayers: {
        zones: false,
        inspections: false,
        tasks: true,
        findings: true,
        assets: false,
        assetTypes: [], // Array of selected asset types
        selectedZones: [], // Array of selected zone IDs
        selectedInspections: [], // Array of selected inspection IDs
      },
      assets: [],
      assetTypes: [],
      currentTileLayerId: "osm",
      currentTileLayerConfig: null,
    };
  },
  computed: {
    workloadTasksCount() {
      return (this.userTasks || []).length;
    },
    workloadOverdueCount() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return (this.userTasks || []).filter((t) => {
        const status = (t.statusFK ?? t.status ?? "").toString().toLowerCase();
        if (status.includes("complete") || status.includes("done") || status.includes("closed")) return false;
        const due = t.dueDate ?? t.DueDate;
        if (!due) return false;
        const d = typeof due === "string" ? new Date(due) : due;
        return !isNaN(d) && d < today;
      }).length;
    },
    coordText() {
      const it = this.selected || {};
      const lat = it.lat ?? it.latitude ?? it.Latitude;
      const lng = it.lng ?? it.longitude ?? it.Longitude;
      if (lat == null || lng == null) return "No coordinates";
      const ff = (n) => (typeof n === "number" ? n.toFixed(5) : String(n));
      return `${ff(+lat)}, ${ff(+lng)}`;
    },
    mapItems() {
      const items = [];

      // Tasks (if layer is visible)
      if (this.mapLayers.tasks) {
      const tasks = (this.tasks || [])
        .map((t) => {
          const lat = t.lat ?? t.latitude ?? t.Latitude;
          const lng = t.lng ?? t.longitude ?? t.Longitude;
          if (lat == null || lng == null) return null;
          return {
            id: t.taskId,
            type: "task",
            title: t.title,
            lat: +lat,
            lng: +lng,
            accuracy: t.accuracy ?? null,
            iconName: t.iconName,
            iconColour: t.iconColour,
          };
        })
        .filter(Boolean);
        items.push(...tasks);
      }

      // Findings (if layer is visible)
      if (this.mapLayers.findings) {
      const findings = (this.findings || [])
        .map((f) => {
          const lat = f.lat ?? f.latitude ?? f.Latitude;
          const lng = f.lng ?? f.longitude ?? f.Longitude;
          if (lat == null || lng == null) return null;
          const visualFinding = this.resolveFindingVisuals(f);

          return {
            id: f.findingId,
            type: "finding",
            title: f.title,
            lat: +lat,
            lng: +lng,
            accuracy: f.accuracy ?? null,
            IconName: visualFinding.IconName,
            iconColour: visualFinding.IconColour,
          };
        })
        .filter(Boolean);
        items.push(...findings);
      }

      // Inspections (if layer is visible) - filter by selected inspections
      if (this.mapLayers.inspections) {
        const inspections = (this.upcomingInspections || [])
          .filter((i) => {
            // Filter by selected inspections if any are selected
            if (this.mapLayers.selectedInspections.length > 0) {
              return this.mapLayers.selectedInspections.includes(i.inspectionId);
            }
            return true; // Show all if none selected
          })
          .map((i) => {
            // Try to get coordinates from zone or inspection itself
            const zone = this.zones.find((z) => z.zoneId === (i.zoneId || i.ZoneId));
            // For now, inspections don't have direct coordinates, so we'll skip them
            // If you want to show inspections, you'd need to add coordinates to the inspection data
            return null;
          })
          .filter(Boolean);
        items.push(...inspections);
      }

      // Assets (if layer is visible)
      if (this.mapLayers.assets) {
        const assets = (this.assets || [])
          .filter((a) => {
            // Filter by asset type if types are selected
            if (this.mapLayers.assetTypes.length > 0) {
              const assetType = a.assetTypeFK || "";
              return this.mapLayers.assetTypes.includes(assetType);
            }
            return true;
          })
          .map((a) => {
            const lat = a.latitude ?? a.Latitude;
            const lng = a.longitude ?? a.Longitude;
            if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;
            return {
              id: `asset-${a.assetId}`,
              type: "asset",
              title: a.assetCode || a.description || `Asset ${a.assetId}`,
              lat: +lat,
              lng: +lng,
              accuracy: null,
              IconName: a.iconName ? `fas fa-${a.iconName.replace(/^fas fa-/, "")}` : "fas fa-map-marker-alt",
              iconColour: a.iconColour || "#9c27b0",
              assetTypeFK: a.assetTypeFK,
            };
          })
          .filter(Boolean);
        items.push(...assets);
      }

      return items;
    },
    filteredMapItems() {
      return this.mapItems;
    },
    filteredGeojson() {
      if (!this.mapLayers.zones) {
        return null;
      }
      
      // If no zones are selected, show all zones
      if (this.mapLayers.selectedZones.length === 0) {
        return this.combinedGeojson;
      }
      
      // Filter geojson to only show selected zones
      const geojson = this.combinedGeojson;
      if (!geojson || !geojson.features) {
        return null;
      }
      
      const selectedZoneIds = new Set(this.mapLayers.selectedZones.map((id) => String(id)));
      const filteredFeatures = geojson.features.filter((feature) => {
        const zoneId =
          feature?.properties?.zoneId ??
          feature?.properties?.ZoneId ??
          feature?.properties?.id;
        return zoneId != null && selectedZoneIds.has(String(zoneId));
      });
      
      return filteredFeatures.length > 0
        ? { type: "FeatureCollection", features: filteredFeatures }
        : null;
    },
    combinedGeojson() {
      const features = [];
      for (const val of Object.values(this.overlays)) {
        if (!val) continue;
        if (val.type === "FeatureCollection" && Array.isArray(val.features)) {
          features.push(...val.features);
        } else if (val.type === "Feature") {
          features.push(val);
        }
      }
      return features.length ? { type: "FeatureCollection", features } : null;
    },
  },
  mounted() {
    this.loadFormVisuals();
    this.fetchTasksDue();
    this.fetchFindingsAction();
    this.fetchupcomingInspections();
    this.loadZones();
    this.fetchRecentUsers();
    this.fetchKPIs();
    this.fetchWorkload();
    this.loadAssets();
    this.loadTaskLookups();
  },
  methods: {
    // Load assignees + task statuses/priorities so the task detail dialog's
    // Edit mode matches the normal task record (Tasks grid).
    async loadTaskLookups() {
      try {
        const [users, statuses, priorities] = await Promise.all([
          teamApi.getUsers(),
          getLookupDataByType("TaskStatus"),
          getLookupDataByType("TaskPriority"),
        ]);
        const toId = (item) => {
          const v = item.value != null && item.value !== "" ? parseInt(item.value, 10) : NaN;
          return Number.isFinite(v) ? v : item.lookupDataId;
        };
        this.assignees = users || [];
        this.taskStatuses = (statuses || [])
          .filter((item) => item.isActive)
          .map((item) => ({
            StatusId: toId(item), Status: item.name,
            statusId: toId(item), status: item.name,
            Colour: item.colour || "",
          }));
        this.taskPriorities = (priorities || [])
          .filter((item) => item.isActive)
          .map((item) => ({
            PriorityId: toId(item), Priority: item.name,
            priorityId: toId(item), priority: item.name,
            Colour: item.colour || "",
          }));
      } catch (e) {
        this.assignees = [];
        this.taskStatuses = [];
        this.taskPriorities = [];
      }
    },
    // A task was completed / saved / deleted from the detail dialog: close it
    // and refresh the dashboard task list so the change is reflected.
    onTaskChanged() {
      this.detailsOpen = false;
      this.fetchTasksDue();
    },
    firstNonEmpty(...values) {
      for (const value of values) {
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return "";
    },
    getFindingFormId(finding) {
      return finding?.formId ?? finding?.FormId ?? null;
    },
    getFormVisualMeta(formId) {
      if (formId == null) return null;
      return this.formVisualsById[String(formId)] || null;
    },
    getLegacyIconName(iconCode) {
      const code = this.firstNonEmpty(iconCode);
      if (!code) return "";
      const idx = iconsNums.findIndex((entry) => entry.icon === code);
      return idx >= 0 ? icons[idx]?.icon || "" : "";
    },
    resolveFindingVisuals(finding) {
      if (!finding || typeof finding !== "object") return finding;
      const formMeta = this.getFormVisualMeta(this.getFindingFormId(finding));
      const iconName =
        this.firstNonEmpty(finding.iconName, finding.IconName, formMeta?.iconName) ||
        this.typeStyles.finding.icon;
      const iconColour =
        this.firstNonEmpty(finding.iconColour, finding.IconColour, formMeta?.iconColour) ||
        this.typeStyles.finding.color;

      return {
        ...finding,
        IconName: iconName,
        IconColour: iconColour,
      };
    },
    async loadFormVisuals() {
      try {
        const forms = await fetchFormsAll();
        this.formVisualsById = Object.fromEntries(
          (forms || [])
            .map((form) => {
              const formId = form.formId ?? form.FormId;
              return [
                String(formId),
                {
                  iconName: this.firstNonEmpty(
                    form.iconName,
                    form.IconName,
                    this.getLegacyIconName(form.icon ?? form.Icon)
                  ),
                  iconColour: this.firstNonEmpty(form.iconColour, form.IconColour),
                },
              ];
            })
            .filter(([formId]) => formId && formId !== "undefined")
        );
        if (this.findings.length > 0) {
          this.findings = this.findings.map((finding) => this.resolveFindingVisuals(finding));
        }
      } catch (err) {
        this.formVisualsById = {};
      }
    },
    onMapReady(map) {
      this.leafletMap = map;
    },
    toggleMapExpanded() {
      this.mapExpanded = !this.mapExpanded;
      this.$nextTick(() => {
        const map = this.$refs.map?.map || this.leafletMap;
        map?.invalidateSize?.(true);
        if (this.mapExpanded) {
          const layer = this.$refs.map?.geojsonLayer;
          const b = layer?.getBounds?.();
          if (b?.isValid?.()) map.fitBounds(b, { padding: [20, 20] });
          else this.$refs.map?.fitToItems?.(0.1);
        }
      });
    },
    async fetchTasksDue() {
      this.loading = true;
      try {
        const resp = await getTasksDue();

        this.tasks = resp.map((i) => ({ ...i }));
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    async fetchFindingsAction() {
      this.loading = true;
      try {
        const resp = await getFindingByStatus(1);
        this.findings = resp.map((i) => this.resolveFindingVisuals({ ...i }));
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    async fetchupcomingInspections() {
      this.loading = true;
      try {
        const resp = await getInspectionByStatus(1);

        this.upcomingInspections = resp.map((i) => ({ ...i }));
        
        // Initialize selected inspections with all inspections when first loaded
        if (this.mapLayers.selectedInspections.length === 0 && this.upcomingInspections.length > 0) {
          this.mapLayers.selectedInspections = this.upcomingInspections.map((i) => i.inspectionId);
        }
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    async fetchRecentUsers() {
      try {
        const list = await teamApi.getUsers();

        const toDate = (v) => {
          if (v == null || v === "") return null;

          // Handle MS AJAX format: /Date(1696800000000)/
          if (typeof v === "string") {
            const m = v.match(/\/Date\((\d+)\)\//);
            if (m) v = Number(m[1]);
          }

          if (typeof v === "number") {
            // If it's a 10-digit-ish epoch (seconds), convert to ms
            const ms = v > 1e12 ? v : v > 1e9 ? v * 1000 : NaN;
            const d = new Date(ms);
            return isNaN(d) || d.getFullYear() < 2000 ? null : d;
          }

          const d = new Date(v);
          return isNaN(d) || d.getFullYear() < 2000 ? null : d;
        };

        const getLastLogin = (u) => {
          const candidates = [
            u.lastLogin,
            u.lastLoginUtc,
            u.lastLoginAt,
            u.lastSignIn,
            u.lastSignInAt,
            u.loginDate,
            u.loginOn,
          ];
          for (const c of candidates) {
            const d = toDate(c);
            if (d) return d;
          }
          return null;
        };

        const top3 = (list || [])
          .map((u) => ({ ...u, _last: getLastLogin(u) }))
          .filter((u) => u._last) // omit users with null/invalid last login
          .sort((a, b) => b._last - a._last) // newest first
          .slice(0, 3)
          .map((u) => ({
            id: u.id || u.userId,
            userName: u.userName || u.name || u.email,
            email: u.email,
            lastLogin: u._last,
            iconId: u.iconId,
          }));

        this.recentUsers = top3;
      } catch (e) {
        this.recentUsers = [];
      }
    },
    async loadZones() {
      try {
        const zonesData = await fetchZones();
        this.zones = zonesData;
        
        // Initialize selected zones with all zones when first loaded
        if (this.mapLayers.selectedZones.length === 0 && zonesData.length > 0) {
          this.mapLayers.selectedZones = zonesData.map((z) => z.zoneId);
        }
        
        for (const z of zonesData) {
          try {
            const resp = await fetchZoneGeoJson(z.zoneId);
            if (!resp) {
              this.overlays = { ...this.overlays, [z.zoneId]: null };
              continue;
            }
            // fetchZoneGeoJson already returns parsed GeoJSON (or null)
            const geojson = this.normalizeZoneGeoJSON(resp, z);
            if (geojson && geojson.features) {
              geojson.features.forEach((feature) => {

                feature.properties = feature.properties || {};
                feature.properties.colour = z.colour || "#FF0000";
                feature.properties.zoneId = z.zoneId; //
                feature.properties.zoneName = z.ZoneName || z.name || z.zoneName;
              });
            }
            // Direct assignment to reactive proxy
            this.overlays = { ...this.overlays, [z.zoneId]: geojson };
          } catch (err) {
            this.overlays = { ...this.overlays, [z.zoneId]: null };
          }
        }
      } catch (err) {
      }
    },
    formatLogin(d) {
      if (!d) return "—";
      const dt = d instanceof Date ? d : new Date(d);
      if (isNaN(dt)) return "—";

      // Absolute time (local)
      const abs = new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(dt);

      // Simple relative time
      const ms = Date.now() - dt.getTime();
      const mins = Math.round(ms / 60000);
      const relFmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
      let rel;
      if (Math.abs(mins) < 60) {
        rel = relFmt.format(-mins, "minute");
      } else {
        const hrs = Math.round(mins / 60);
        if (Math.abs(hrs) < 24) rel = relFmt.format(-hrs, "hour");
        else rel = relFmt.format(-Math.round(hrs / 24), "day");
      }

      return `${abs} (${rel})`;
    },
    getUserIconUrl(id) {
      // default to 1 if invalid, pad to two digits
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    goToInspection({ id, item }) {
      // Be flexible about the primary key name coming from your API
      const inspId = id ?? item?.inspectionId ?? item?.InspectionId ?? item?.id;

      if (!inspId) {
        return;
      }

      // SPA navigation using your named route
      this.$router.push({
        name: "InspectionEdit",
        params: { id: inspId },
      });

      // If you truly need a full page load instead:
      // window.location.assign(`${window.location.origin}/inspections/${inspId}/edit`);
    },

    onEditTask() {},
    focusFromGrid({ id, type }) {
      // optional: pan/zoom to the marker & open any popup
      this.$refs.map.highlight(id);
    },
    onMarkerClick({ id, type, item }) {
      this.$refs.map.highlight(id);
      // open side drawer, etc.
    },
    openDetails({ id, type /*, item*/ }) {
      let found = null;

      if (type === "task") {
        found = (this.tasks || []).find((t) => t.taskId === id);
      } else if (type === "finding") {
        found = (this.findings || []).find((f) => f.findingId === id);
      }

      // Fallback (in case arrays not populated yet):
      // if (!found && item) found = item;

      if (!found) return;

      const lat = found.lat ?? found.latitude ?? found.Latitude;
      const lng = found.lng ?? found.longitude ?? found.Longitude;

      this.selected = { ...this.resolveFindingVisuals(found), lat: +lat, lng: +lng };
      this.selectedType = type;
      this.hoverId = id;
      this.detailsOpen = true;
      this.$refs.map?.highlight?.(id);
    },

    // wire existing callbacks to open modal
    focusFromGrid(payload) {
      this.openDetails(payload);
    },
    onMarkerClick(payload) {
      this.openDetails(payload);
    },
    formatDate(v) {
      if (!v) return "—";

      // If it's already a Date
      if (v instanceof Date && !isNaN(v)) {
        return v.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }

      // If it's ISO-ish
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        const d = new Date(v);
        if (!isNaN(d)) {
          return d.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
      }

      // If it's dd/MM/yyyy or dd/MM/yy
      if (typeof v === "string") {
        const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
        if (m) {
          let [_, dd, mm, yy] = m;
          const day = parseInt(dd, 10);
          const mon = parseInt(mm, 10) - 1;
          let yr = parseInt(yy, 10);
          if (yy.length === 2) yr += yr >= 70 ? 1900 : 2000; // naive pivot
          const d = new Date(yr, mon, day);
          if (!isNaN(d)) {
            return d.toLocaleDateString(undefined, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
          }
        }
      }

      // Fallback: show raw
      return String(v);
    },
    // Prefer zoneId on inspections; fall back to zoneName if needed.
    getInspectionZoneId(row) {
      if (!row) return null;
      return row.zoneId ?? row.ZoneId ?? row.zoneFK ?? null;
    },
    getInspectionZoneName(row) {
      if (!row) return null;
      return row.zoneName ?? row.zoneNameFK ?? row.ZoneName ?? null;
    },
    sameId(a, b) {
      return a != null && b != null && String(a) === String(b);
    },
    onInspectionRowEnter(evt) {
      const id =
        evt?.id ??
        evt?.inspectionId ??
        evt?.InspectionId ??
        (typeof evt === "number" || typeof evt === "string" ? evt : null);

      const row =
        evt?.item ||
        (this.upcomingInspections || []).find((r) =>
          this.sameId(r.inspectionId ?? r.InspectionId, id)
        );

      this.hoverInspId = id ?? row?.inspectionId ?? row?.InspectionId ?? null; // single-row highlight
      this.hoverInspIds = []; // clear multi highlight so only the hovered row glows

      const zoneId = this.getInspectionZoneId(row);
      if (zoneId != null) {
        this.hoverZoneId = zoneId; // map highlights that polygon
        return;
      }
      const zName = this.getInspectionZoneName(row);
      if (zName) {
        const match = (this.zones || []).find(
          (z) => (z.ZoneName || z.zoneName || z.name) === zName
        );
        if (match) this.hoverZoneId = match.zoneId;
        return;
      }
      this.hoverZoneId = null;
    },

    onInspectionRowLeave() {
      this.hoverZoneId = null;
      this.hoverInspId = null;
      this.hoverInspIds = [];
    },

    // When you hover a polygon, highlight ALL inspections in that zone
    onShapeHover({ id, properties }) {
      this.hoverZoneId = id;

      // all rows whose zoneId matches the polygon
      const all = (this.upcomingInspections || []).filter((r) =>
        this.sameId(this.getInspectionZoneId(r), id)
      );

      // fallback by zoneName if needed
      const byName = (this.upcomingInspections || []).filter((r) => {
        const zName = this.getInspectionZoneName(r);
        return zName && (zName === properties?.zoneName || zName === properties?.ZoneName);
      });

      const union = all.length ? all : byName;

      // many-row highlight
      this.hoverInspIds = union.map((r) => r.inspectionId ?? r.InspectionId);

      // clear single-id so only the array drives the styling
      this.hoverInspId = null;
    },

    onShapeLeave() {
      this.hoverZoneId = null;
      this.hoverInspId = null;
      this.hoverInspIds = [];
    },
    onShapeClick({ id, properties }) {
      // Optional: focus/open an inspection for this zone on click
      //const candidate =
      //  (this.upcomingInspections || []).find((r) => this.getInspectionZoneId(r) === id) || null;
      //if (candidate) {
      //  this.goToInspection({ id: candidate.inspectionId, item: candidate });
      //}

      this.$router.push({
        name: "InspectionList",
      });
    },
    normalizeZoneGeoJSON(raw, z) {
      // parse strings
      let gj = raw;
      if (typeof raw === "string") {
        try {
          gj = JSON.parse(raw);
        } catch (e) {
          return null;
        }
      }

      if (!gj) return null;

      // Wrap single Feature into a FeatureCollection
      if (gj.type === "Feature") {
        gj = { type: "FeatureCollection", features: [gj] };
      }

      if (gj.type === "FeatureCollection" && Array.isArray(gj.features)) {
        gj.features.forEach((f) => {
          f.properties = f.properties || {};
          f.properties.zoneId = z.zoneId; // KEY for highlighting
          f.properties.zoneName = z.ZoneName || z.name || z.zoneName || "";
          f.properties.colour = z.Colour || "#FF0000";
        });
        return gj;
      }

      return null;
    },
    async fetchWorkload() {
      this.loadingWorkload = true;
      try {
        const [tasks, syncData] = await Promise.all([
          getTasksByUser(),
          dashboardApi.getLastMobileSync(),
        ]);
        this.userTasks = tasks || [];
        this.lastMobileSyncAt = syncData?.lastSyncAt ?? null;
      } catch (err) {
        this.userTasks = [];
        this.lastMobileSyncAt = null;
      } finally {
        this.loadingWorkload = false;
      }
    },
    formatLastMobileSync(val) {
      if (!val) return "—";
      const d = typeof val === "string" ? new Date(val) : val;
      if (isNaN(d)) return "—";
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(d);
    },
    async fetchKPIs() {
      this.loadingKpis = true;
      try {
        const data = await dashboardApi.getKpis();
        this.kpis = {
          overdueTasksCount: data.overdueTasksCount || 0,
          averageTaskResolutionDays: data.averageTaskResolutionDays || 0,
          tasksCompletedThisWeek: data.tasksCompletedThisWeek || 0,
          highPriorityOverdueCount: data.highPriorityOverdueCount || 0,
          workOrdersClosedThisWeek: data.workOrdersClosedThisWeek || 0,
          activeWorkOrdersCount: data.activeWorkOrdersCount || 0,
          overdueWorkOrdersCount: data.overdueWorkOrdersCount || 0,
          workOrderOnTimeRate: data.workOrderOnTimeRate || 0,
          pendingFindingsCount: data.pendingFindingsCount || 0,
        };
      } catch (err) {
        // Keep default values on error
      } finally {
        this.loadingKpis = false;
      }
    },
    formatPercentage(value) {
      if (value == null || isNaN(value)) return "0%";
      return `${Math.round(value)}%`;
    },
    formatDays(value) {
      if (value == null || isNaN(value) || value === 0) return "N/A";
      const days = Math.round(value * 10) / 10; // Round to 1 decimal
      return days === 1 ? "1 day" : `${days} days`;
    },
    onTileLayerChange(tileLayer) {
      this.currentTileLayerId = tileLayer.id;
      this.currentTileLayerConfig = tileLayer;
    },
    async loadAssets() {
      try {
        const response = await pmApi.getAssetsMap();
        const raw = response.data || [];
        this.assets = raw
          .map((a) => ({
            assetId: a.assetId ?? a.AssetId,
            assetCode: a.assetCode ?? a.AssetCode,
            description: a.description ?? a.Description,
            latitude:
              typeof a.Latitude === "string" ? parseFloat(a.Latitude) : a.latitude ?? a.Latitude,
            longitude:
              typeof a.Longitude === "string"
                ? parseFloat(a.Longitude)
                : a.longitude ?? a.Longitude,
            iconName: a.iconName ?? a.IconName,
            iconColour: a.iconColour ?? a.IconColour,
            assetTypeFK: a.assetTypeFK ?? a.AssetTypeFK,
          }))
          .filter((a) => !isNaN(a.latitude) && !isNaN(a.longitude));

        // Build asset types list
        const types = new Set();
        for (const a of this.assets) {
          if (a.assetTypeFK) types.add(a.assetTypeFK);
        }
        this.assetTypes = Array.from(types).sort((a, b) => (a || "").localeCompare(b || ""));

        // If assets layer is enabled, select all types by default
        if (this.mapLayers.assets && this.mapLayers.assetTypes.length === 0 && this.assetTypes.length > 0) {
          this.mapLayers.assetTypes = [...this.assetTypes];
        }
      } catch (err) {
        this.assets = [];
        this.assetTypes = [];
      }
    },
  },
};
</script>
<style scoped>
:deep(.task-table .v-table .v-table__wrapper > table > tbody > tr) {
}
:deep(.task-table .v-table .v-table__wrapper > table > tbody > tr:hover > td) {
  background-color: var(--row-hover, rgba(0, 0, 0, 0.06));
}
:deep(.task-table .v-table .v-table__wrapper > table > tbody > tr:hover) {
  cursor: pointer;
}
:deep(.row-hot) {
  background: rgba(42, 114, 207, 0.1);
  border-radius: 6px;
}

.v-list-item-title {
  font-size: 0.7rem;
}
.v-list-item-subtitle {
  font-size: 0.6rem;
}
/* KPI list styling */
.kpi-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
  font-size: 0.75rem;
}
.kpi-list li {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(2px);
}
.kpi-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  opacity: 0.95;
}
.kpi-value {
  font-weight: 800;
  letter-spacing: 0.2px;
}
.kpi-caption {
  opacity: 0.8;
  font-size: 0.7rem;
}
.kpi-list li.kpi-wo-block {
  display: block;
  padding: 6px 8px;
}
.kpi-wo-header {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.95;
  margin-bottom: 4px;
}
.kpi-wo-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.kpi-wo-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px 4px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
}
.kpi-wo-label {
  font-size: 0.65rem;
  opacity: 0.85;
  line-height: 1.1;
}
.kpi-wo-value {
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: 0.2px;
  line-height: 1.2;
}
.kpi-wo-value.text-error {
  color: rgb(var(--v-theme-error));
}

/* My Workload list */
.workload-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
  font-size: 0.75rem;
  color: white;
}
.workload-list li {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(2px);
}
.workload-list li .workload-row {
  display: contents;
}
.workload-list a {
  color: white;
  text-decoration: none;
}
.workload-list a:hover {
  text-decoration: underline;
}
.workload-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  opacity: 0.95;
  color: white;
}
.workload-value {
  font-weight: 800;
  letter-spacing: 0.2px;
  color: white;
  text-align: right;
}
.workload-value.text-error {
  color: rgb(var(--v-theme-error));
}
.workload-value.text-warning {
  color: rgb(var(--v-theme-warning));
}
.workload-caption {
  opacity: 0.9;
  font-size: 0.7rem;
  color: white;
  text-align: right;
}
.workload-link {
  display: contents;
  color: inherit;
  text-decoration: none;
}
.workload-link:hover {
  text-decoration: underline;
}

.map-wrapper {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 420px;
  border-radius: 10px;
  overflow: hidden;
}
.map-wrapper.is-expanded {
  position: fixed;
  inset: 0;
  z-index: 2000; /* above page content */
  background: #fff;
  border-radius: 0;
}
.map-expand-btn {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 3000; /* above Leaflet controls */
  backdrop-filter: blur(6px);
}
i.mdi-fullscreen.mdi.v-icon.notranslate.v-theme--myCustomLightTheme.v-icon--size-default,
i.mdi-fullscreen-exit.mdi.v-icon.notranslate.v-theme--myCustomLightTheme.v-icon--size-default {
  background-color: white;
  font-size: 40px;
}

/* Position Map Style control just below the expand button */
:deep(.map-tile-control-overview.map-tile-layer-control) {
  position: absolute !important;
  top: 60px !important;
  right: 10px !important;
  left: auto !important;
  z-index: 1000;
}
</style>
